// components/chat/AudioBolha.js — o áudio dentro da bolha (out/26, redesign
// "Chat Cetto.dc.html").
//
// Era o <audio controls> do navegador: cinza-claro no tema escuro, com cara
// diferente em cada navegador e largo demais pra bolha. Aqui é o desenho do
// WhatsApp — tocar, barra, tempo — com as cores do chat, e uma velocidade
// (1×, 1,5×, 2×) porque atendente ouve muito áudio longo.
//
// A barra é um <input type="range">: arrasta com o mouse e anda com as
// setas do teclado, sem reinventar acessibilidade.
//
// Áudio gravado pelo navegador (webm do MediaRecorder) chega sem duração no
// cabeçalho: o `duration` vem Infinity. O truque de pular pro fim faz o
// navegador ler o arquivo e descobrir; depois volta pro começo.
import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { FiPlay, FiPause } from 'react-icons/fi';

const VELOCIDADES = [1, 1.5, 2];

const fmt = (s) => {
  if (!Number.isFinite(s) || s < 0) return '0:00';
  const t = Math.floor(s);
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
};

export default function AudioBolha({ src, enviada = false }) {
  const ref = useRef(null);
  const descobrindo = useRef(false);
  const [tocando, setTocando] = useState(false);
  const [duracao, setDuracao] = useState(0);
  const [pos, setPos] = useState(0);
  const [vel, setVel] = useState(1);

  useEffect(() => {
    const el = ref.current;
    if (el) el.playbackRate = vel;
  }, [vel]);

  const aoCarregar = () => {
    const el = ref.current;
    if (!el) return;
    if (el.duration === Infinity || Number.isNaN(el.duration)) {
      descobrindo.current = true;
      el.currentTime = 1e101;
      return;
    }
    setDuracao(el.duration);
  };
  const aoAndar = () => {
    const el = ref.current;
    if (!el) return;
    if (descobrindo.current) {
      descobrindo.current = false;
      setDuracao(Number.isFinite(el.duration) ? el.duration : 0);
      el.currentTime = 0;
      return;
    }
    setPos(el.currentTime);
  };

  const alternar = () => {
    const el = ref.current;
    if (!el) return;
    if (el.paused) el.play().catch(() => {});
    else el.pause();
  };

  const legenda = enviada ? 'var(--ch-ob-m)' : 'var(--ch-cap)';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', width: { xs: 220, sm: 250 }, maxWidth: '100%' }}>
      <audio
        ref={ref}
        src={src}
        preload="metadata"
        onLoadedMetadata={aoCarregar}
        onDurationChange={() => {
          const el = ref.current;
          if (el && Number.isFinite(el.duration) && !descobrindo.current) setDuracao(el.duration);
        }}
        onTimeUpdate={aoAndar}
        onPlay={() => setTocando(true)}
        onPause={() => setTocando(false)}
        onEnded={() => { setTocando(false); setPos(0); }}
      />
      <Box
        component="button" type="button" onClick={alternar}
        aria-label={tocando ? 'Pausar áudio' : 'Ouvir áudio'}
        sx={{
          width: 36, height: 36, borderRadius: '50%', border: 0, p: 0, flexShrink: 0, cursor: 'pointer',
          bgcolor: 'var(--ch-green)', color: 'var(--ch-sobre-verde)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          '&:hover': { bgcolor: 'var(--ch-green-hover)' },
          '&:focus-visible': { outline: '2px solid var(--ch-green)', outlineOffset: 2 },
        }}
      >
        {tocando ? <FiPause size={15} /> : <FiPlay size={15} style={{ marginLeft: 2 }} />}
      </Box>
      <Box
        component="input" type="range" min={0} max={duracao || 0} step="any" value={Math.min(pos, duracao || 0)}
        aria-label="Posição do áudio"
        aria-valuetext={`${fmt(pos)} de ${fmt(duracao)}`}
        onChange={(e) => {
          const el = ref.current;
          const v = Number(e.target.value);
          if (el) el.currentTime = v;
          setPos(v);
        }}
        sx={{
          flex: 1, minWidth: 0, height: 16, m: 0, cursor: 'pointer', background: 'transparent',
          WebkitAppearance: 'none', appearance: 'none',
          '--p': duracao ? `${(Math.min(pos, duracao) / duracao) * 100}%` : '0%',
          '&::-webkit-slider-runnable-track': {
            height: 3, borderRadius: 999,
            background: `linear-gradient(to right, var(--ch-green-d) var(--p), ${enviada ? 'var(--ch-ob-b)' : 'var(--ch-bdr2)'} var(--p))`,
          },
          '&::-moz-range-track': { height: 3, borderRadius: 999, background: enviada ? 'var(--ch-ob-b)' : 'var(--ch-bdr2)' },
          '&::-moz-range-progress': { height: 3, borderRadius: 999, background: 'var(--ch-green-d)' },
          '&::-webkit-slider-thumb': {
            WebkitAppearance: 'none', width: 11, height: 11, borderRadius: '50%', mt: '-4px',
            background: 'var(--ch-green-d)', border: 0,
          },
          '&::-moz-range-thumb': { width: 11, height: 11, borderRadius: '50%', background: 'var(--ch-green-d)', border: 0 },
          '&:focus-visible': { outline: '2px solid var(--ch-green)', outlineOffset: 2, borderRadius: 4 },
        }}
      />
      <Box component="span" sx={{ fontSize: 12.5, color: legenda, fontVariantNumeric: 'tabular-nums', flexShrink: 0, minWidth: 30, textAlign: 'right' }}>
        {fmt(tocando || pos > 0 ? pos : duracao)}
      </Box>
      <Box
        component="button" type="button"
        onClick={() => setVel((v) => VELOCIDADES[(VELOCIDADES.indexOf(v) + 1) % VELOCIDADES.length])}
        aria-label={`Velocidade ${String(vel).replace('.', ',')} vezes. Trocar`}
        sx={{
          height: 22, minWidth: 34, px: '6px', borderRadius: 999, flexShrink: 0, cursor: 'pointer',
          border: `1px solid ${enviada ? 'var(--ch-ob-b)' : 'var(--ch-bdr2)'}`, bgcolor: 'transparent',
          color: legenda, fontFamily: 'inherit', fontSize: 11.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
          '&:hover': { color: 'var(--ch-text)' },
          '&:focus-visible': { outline: '2px solid var(--ch-green)', outlineOffset: 2 },
        }}
      >
        {String(vel).replace('.', ',')}×
      </Box>
    </Box>
  );
}
